import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Classifications } from 'projects/money-movement-ui/src/app/account-info-header/models';
import { selectClassifications, selectIsIra, selectIsPtrs, selectRegistrationType, selectRegType } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { FirstPartiesOutput } from 'projects/money-movement-ui/src/app/modified-first-parties/models/FirstParties';
import { selectModifiedFirstParties } from 'projects/money-movement-ui/src/app/modified-first-parties/reducers';
import { Tooltip } from 'projects/money-movement-ui/src/app/shared/models/Tooltip';
import { RmdAmountFieldInfo } from 'projects/money-movement-ui/src/app/shared/types/RmdAmountFieldInfo';
import { AmountType } from 'projects/money-movement-ui/src/app/shared/types/Transaction';
import { ApiEndpointType, Constants, Frequencies, RecurringFrequencyCodes, amountTypeDict, distributionReasons, fedTaxErrorMap, frequencyDict, recipientType, yesNo } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { mmddyyyyWithSlashes } from 'projects/money-movement-ui/src/app/shared/utilities/regex-patterns';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, takeUntil, withLatestFrom } from 'rxjs/operators';
import { FormatDatePipe } from '../../../../shared/pipes/FormatDatePipe';
import * as CustomValidators from '../../../../shared/utilities/FormValidators';
import { TransactionFormActions } from '../../../actions';
import { CheckFeeDetails, FirmDetails, RecipientAddressDetails } from '../../../models/check/CheckData';
import { CheckTransactionDetails, TaxWithholdingFields, TransactionForm, TransactionFormServiceError } from '../../../models/form/TransactionForm';
import { DistributionReason } from '../../../models/internal-transfer/DistributionReason';
import { CodeAndDescription } from '../../../models/shared/CodeAndDescription';
import { selectCheckAvailableCashMMF, selectCheckAvailableCashMargin, selectCheckCountries, selectCheckDistributionReason, selectCheckDistributionReasonMap, selectCheckEndDate, selectCheckFee, selectCheckFeeDetails, selectCheckInfo, selectCheckInputCallFinished, selectCheckIsRmdEligible, selectCheckPayIncome, selectCheckPayee, selectCheckProcessDate, selectCheckReasonMap, selectCheckRecipientSelection, selectCheckRecurringFrequency, selectCheckSecondProcessDate, selectCheckServiceError, selectCheckTaxWithholding, selectCheckUseStandingAuth, selectContinueClickTotalDistTriggered, selectCountryCode, selectDefaultPayee, selectDefaultRecipient, selectFirmDetails, selectFrequency, selectFrequencyList, selectIsFromEdit, selectIsFromEditSubmittedTransaction, selectIsRecurring, selectModifiedPayeeCheckbox, selectOpenTwePopupConditions, selectPayeeType, selectStateList, selectTranasctionFormProcessDate, selectTransactionFormHasError, selectTransactionFormServiceError, selectTransactionFormTransactionDetails, selectUseStandingAuth } from '../../../reducers';
import { currencyStringToNumber } from '../../../utilities/currencyStringToNumber';
import { buildFromPrefilledForm } from '../../../utilities/formUtilities';
import { CheckTransactionDetailsForm, ExtraPromptsForm, RecipientValues } from '../../../models/form/TransactionFormGroup';
import { selectEnableCodeY4RecurringChecks, selectIsEnableRecurringChecksStandingAuth } from 'projects/money-movement-ui/src/app/feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-check',
  templateUrl: './transaction-details-check.component.html',
  styleUrls: ['./transaction-details-check.component.scss']
})
export class TransactionDetailsCheckComponent implements OnInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPrompts: CheckTransactionDetails;
  @Input() checkTransactionType: string;
  @Input() editFormDetails: TransactionForm | undefined;
  @Input() isEditRecurringSubmittedCheckTransaction: boolean = false;
  @Output() overnightCheckFailure = new EventEmitter<boolean>();
  amountType: AmountType = amountTypeDict.gross;
  checkFee$: Observable<number>;
  checkFeeDetails$: Observable<CheckFeeDetails>;
  checkReasonMap$: Observable<CodeAndDescription[]>;
  processDateLabel: string = 'Process date';
  secondProcessDateLabel: string = Constants.secondProcessDateLabel;
  endDateLabel: string = Constants.endDateLabel;
  processDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
    CustomValidators.notAWeekend
  ];
  recurringProcessDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
  ];
  secondProcessDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  endDateValidators: ValidatorFn[] = [
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  datePickertooltip: Tooltip = {
    position: 'right',
    containsLink: false,
    content: Constants.checkProcessDateToolTip
  };
  defaultPayee$: Observable<string[]>;
  defaultRecipient$: Observable<string[]>;
  destroy$: Subject<boolean> = new Subject();
  showY4CodeStandingAuth: boolean;
  distributionReason: DistributionReason;
  distributionReasonMap$: BehaviorSubject<DistributionReason[]> = new BehaviorSubject<DistributionReason[]>([]);
  hasServiceError: boolean;
  isIra$: Observable<boolean>;
  payeeType$: Observable<string>;
  recipientSelection$: Observable<string>;
  stateList$: Observable<CodeAndDescription[]>;
  taxWithholdings: TaxWithholdingFields;
  transactionDetailsGroup: FormGroup<CheckTransactionDetailsForm>;
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  validPayee: boolean;
  validRecipient: boolean;
  isRecurring$: Observable<boolean>;
  isRecurringTransaction: boolean;
  frequencyList$: Observable<CodeAndDescription[]>;
  showSecondProcessDate: boolean;
  showEndDate: boolean;
  showPayIncome: boolean;
  payIncome$: Observable<string>;
  secondProcessDate$: Observable<string>;
  endDate$: Observable<string>;
  recurringFrequency$: Observable<string>;
  countryList$: Observable<CodeAndDescription[]>;
  availableCash: number;
  isPtrs: boolean;
  firmAddress$: Observable<FirmDetails>;
  receipt$: Observable<RecipientAddressDetails>;
  isFromEdit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  warningText: any;
  inputCallFinished$: Observable<boolean>;
  classifications$: Observable<Classifications>;
  classifications: Classifications;
  displayRmdAmount: BehaviorSubject<RmdAmountFieldInfo> = new BehaviorSubject<RmdAmountFieldInfo>({ displayRmdAmount: false, rmdAmount: '0' });
  autoCalculateRmd$: Observable<boolean>;
  useSloa$: Observable<boolean>;
  useStandingAuth: boolean;
  countryCode: string;
  serverErrorText: string;
  hasServerError: boolean;
  formatDatePipe = new FormatDatePipe();
  defaultProcessDate: string;
  distributionReasonTooltip: string = Constants.itCheckDistributionReasonTooltip;
  distributionReasonTooltipHtml: string = Constants.qcdDistributionReasonTooltip;
  useModifiedPayee: boolean = false;
  firstPartyPayees$: Observable<FirstPartiesOutput[]>;
  useConstructedPayee: boolean;
  isFirstPartyModified: boolean;
  isFromEditTable: boolean;
  recipientAddressType: string;
  showModifiedPayeeBanner: boolean = false;
  jointAcctModifiedPayeeBannerText: string = Constants.jointPayeeBannerText;
  showOvernightWarning: boolean;
  distributionReason$: Observable<DistributionReason>;
  frequency$: Observable<'recurring' | 'onetime' | 'onetimeTotalDist' | undefined> ;
  hasInternationalFedTaxError: boolean = false;
  readonly bundled: string = 'bundled';
  readonly onetimeTotalDist: string = 'onetimeTotalDist';

  private store: Store = inject(Store);
  private fb: FormBuilder = inject(FormBuilder);

  ngOnInit(): void {
    this.transactionDetailsGroup = this.fb.nonNullable.group({});
    this.parentForm.setControl('transactionDetails', this.transactionDetailsGroup, { emitEvent: false });

    this.transactionFormServiceError$ = this.store.select(selectTransactionFormServiceError);
    this.transactionFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe((val: TransactionFormServiceError) => {
      if (!(val?.name === ApiEndpointType.checkVerify)) {
        if (val?.status?.toString().startsWith('4') || val?.status?.toString().startsWith('5')) {
          this.hasServiceError = true;
          setTimeout(() => {
            const bannerEl = document.getElementById('transactionDetailsCheck_serviceError');
            bannerEl?.focus();
            bannerEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
          }, 0);
        } else {
          this.hasServiceError = false;
        }
      }
    });

    this.store.select(selectPayeeType).pipe(takeUntil(this.destroy$)).subscribe(payeeType => {
      if (payeeType === 'thirdParty') {
        this.useModifiedPayee = false;
        let modifiedCheckbox = this.parentForm.controls.transactionDetails.get('modifiedPayeeCheckbox') as FormControl<boolean>;
        modifiedCheckbox?.reset();
      }
    });

    // When input or verify call is successful but the response contains an error like insufficient funds
    this.store.select(selectCheckServiceError).pipe(takeUntil(this.destroy$)).subscribe(serviceError => {
      if (serviceError) {
        this.hasServerError = true;
        this.hasInternationalFedTaxError = serviceError.toLowerCase() === fedTaxErrorMap.cashieringinternationFedTaxError.toLowerCase();
        this.serverErrorText = serviceError;
        setTimeout(() => {
          const bannerEl = document.getElementById('server-error-banner');
          bannerEl?.focus();
          bannerEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
        }, 0);
      } else {
        this.hasServerError = false;
        this.serverErrorText = '';
      }
    });

    combineLatest([
      this.store.select(selectRegistrationType),
      this.store.select(selectModifiedPayeeCheckbox)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([type, checked]) => {
        this.useModifiedPayee = checked ?? this.useModifiedPayee;
        this.showModifiedPayeeBanner = type === 'Jt Ten' && checked ? true : false;
      });

    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(_isPtrs => this.isPtrs = _isPtrs);
    combineLatest([
      this.store.select(selectCheckAvailableCashMMF),
      this.store.select(selectCheckAvailableCashMargin)
    ]).pipe(
      takeUntil(this.destroy$),
      map(([availableCashMMF, availableCashMargin]) => {
        this.availableCash = currencyStringToNumber(availableCashMargin) + currencyStringToNumber(availableCashMMF);
      })
    );

    this.payeeType$ = this.store.select(selectPayeeType);
    this.defaultPayee$ = this.store.select(selectDefaultPayee);
    combineLatest([
      this.store.select(selectCheckInfo),
      this.store.select(selectIsFromEditSubmittedTransaction)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([info, isFromEditTable]) => {
        this.isFromEditTable = isFromEditTable;
        this.useConstructedPayee = info.useConstructedPayee;
        this.isFirstPartyModified = info.isFirstPartyModified;
        this.recipientAddressType = info.cashieringCheckInputDetails.cashieringAddressDetails.recipientAddressType;
        this.defaultPayee$ = combineLatest([
          this.store.select(selectDefaultPayee),
          this.store.select(selectCheckPayee),
          this.store.select(selectModifiedPayeeCheckbox)
        ]).pipe(
          map(([defaultPayee, checkPayee, modifiedPayeeCheckbox]) => {
            const hasCheckPayee = (checkPayee ?? []).some((payee: string) => !!payee?.trim());
            const shouldUseCheckPayee = !this.useConstructedPayee && hasCheckPayee && !modifiedPayeeCheckbox;
            return shouldUseCheckPayee ? checkPayee : defaultPayee;
          })
        );
      });

    this.recipientSelection$ = this.store.select(selectCheckRecipientSelection);
    this.defaultRecipient$ = this.store.select(selectDefaultRecipient);

    this.firmAddress$ = this.store.select(selectFirmDetails);
    this.isIra$ = this.store.select(selectIsIra);
    this.checkFeeDetails$ = this.store.select(selectCheckFeeDetails);
    this.checkReasonMap$ = this.store.select(selectCheckReasonMap);
    this.checkFee$ = this.store.select(selectCheckFee);
    this.stateList$ = this.store.select(selectStateList);
    this.isRecurring$ = this.store.select(selectIsRecurring);
    this.frequencyList$ = combineLatest([
      this.store.select(selectFrequencyList),
      this.store.select(selectIsEnableRecurringChecksStandingAuth),
      this.store.select(selectCheckUseStandingAuth),
      this.store.select(selectFrequency)
    ]).pipe(
      map(([frequencyList, enableRecurringChecksStandingAuth, checkUseStandingAuth, frequency]) => {
        if (!frequencyList) {
          return [];
        }
        const isSloaorBsloa= checkUseStandingAuth === yesNo.yes || checkUseStandingAuth === 'bundled-yes';
        // suppress 2times/month  for recurring SLOA/BSLOA when feature flag is enabled
        if (enableRecurringChecksStandingAuth && isSloaorBsloa && frequency === Frequencies.recurring) {
          return frequencyList?.filter(f => f?.code !== RecurringFrequencyCodes.twiceMonthly);
        }
        return frequencyList;
      })
    );
    this.payIncome$ = this.store.select(selectCheckPayIncome);
    this.secondProcessDate$ = this.store.select(selectCheckSecondProcessDate);
    this.endDate$ = this.store.select(selectCheckEndDate);
    this.recurringFrequency$ = this.store.select(selectCheckRecurringFrequency);
    this.countryList$ = this.store.select(selectCheckCountries);
    this.inputCallFinished$ = this.store.select(selectCheckInputCallFinished);
    this.distributionReason$ = this.store.select(selectCheckDistributionReason);
    this.frequency$ = this.store.select(selectFrequency);

    combineLatest([
      this.store.select(selectCheckTaxWithholding),
      this.store.select(selectCheckDistributionReason)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([taxWithholdings, distributionReason]) => {
        this.distributionReason = distributionReason;
        this.taxWithholdings = taxWithholdings;
      });

    combineLatest([
      this.store.select(selectCheckDistributionReasonMap),
      this.store.select(selectPayeeType),
      this.store.select(selectRegType),
      this.store.select(selectEnableCodeY4RecurringChecks)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([distributionReasonList, payeeType, regType, enableCodeY4RecurringChecks]) => {
        let mappedReasons = [...distributionReasonList];
        let frequency = this.getFrequency(this.extraPrompts, this.getFrequencyType(this.extraPrompts));
        let onRequest = ['onetime', 'One-time'];
        let isQcdY4EnabledForRecurring = enableCodeY4RecurringChecks && frequency.toLowerCase() === frequencyDict.recurring.toLowerCase();


        if (payeeType === recipientType.firstParty) {
          mappedReasons = [];
          this.showY4CodeStandingAuth = false;
          distributionReasonList.forEach((ele) => {
            if (ele.description !== distributionReasons.qcd && ele.description !== distributionReasons.qcdY4) {
              mappedReasons.push(ele);
            }
          });
        }
        if (payeeType === recipientType.thirdParty && (regType === 'Inh IRA' || regType === 'Inh IRA QP')) {
          this.showY4CodeStandingAuth = true;
          if (!onRequest.includes(frequency) && !isQcdY4EnabledForRecurring) { // OnetimeTotalDist, we are not adding Y4 code in distributionReasons dropdownlist
            this.showY4CodeStandingAuth = false;
            const index = mappedReasons?.findIndex(distReasons => distReasons.code === 'Y4');
            if (index !== -1) {
              mappedReasons.splice(index, 1);
            }
          }
        }
        if (payeeType === recipientType.thirdParty && regType !== 'Inh IRA' && regType !== 'Inh IRA QP') {
          mappedReasons = [];
          this.showY4CodeStandingAuth = false;
          distributionReasonList.forEach((ele) => {
            if (ele.description !== distributionReasons.qcdY4) {
              mappedReasons.push(ele);
            }
          });
        }
        if (Frequencies.recurring.toUpperCase() === frequency?.toUpperCase() && (regType.toUpperCase() === 'ROLLOVER' || regType.toUpperCase() === 'CONTRIB' || regType.toUpperCase() === 'SEP' || regType.toUpperCase() === 'SIMPLE IRA')) {
          if (payeeType === recipientType.firstParty) { // recurring transaction with first party payee, not display 7QCD code in distributionReasons dropdownlist
            const index = mappedReasons?.findIndex(distReasons => distReasons.description === distributionReasons.qcd);
            if (index !== -1) {
              mappedReasons.splice(index, 1);
            }
          }
        }

        if (isQcdY4EnabledForRecurring && payeeType === recipientType.thirdParty && regType.toLowerCase() !== 'inh ira' && regType.toLowerCase() !== 'inh ira qp') {
          this.showY4CodeStandingAuth = false;
          const index = mappedReasons?.findIndex(distReasons => distReasons?.code === 'Y4');
          if (index !== -1) {
            mappedReasons.splice(index, 1);
          }
        }

        this.distributionReasonMap$.next(mappedReasons);
      });

    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(val => { this.classifications = val; });
    this.store.select(selectCountryCode).pipe(takeUntil(this.destroy$)).subscribe(val => this.countryCode = val);

    // SET PROCESS DATE
    this.store.select(selectCheckProcessDate).pipe(takeUntil(this.destroy$)).subscribe(processDate => this.defaultProcessDate = this.formatDatePipe.transform(processDate));

    // If recurring and rmdEligible and 7 RMD selected and process date is future year, then set the amount to zero
    this.autoCalculateRmd$ = combineLatest([
      this.store.select(selectIsRecurring),
      this.store.select(selectCheckIsRmdEligible),
      this.store.select(selectCheckTaxWithholding),
      this.store.select(selectTranasctionFormProcessDate),
    ]).pipe(map(([isRecurring, isRmdEligible, taxWithholdings, processDate]) => isRecurring && isRmdEligible && taxWithholdings?.distributionReason === distributionReasons.rmd && this.isProcessDateInFutureYear(processDate!)));

    this.useSloa$ = this.store.select(selectUseStandingAuth);
    this.useSloa$.pipe(takeUntil(this.destroy$)).subscribe(useStandingAuth => this.useStandingAuth = useStandingAuth);

    // Only reset form when isFromEdit is false
    this.extraPrompts?.valueChanges.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
      ),
      filter(([_value, isFromEdit]) => !isFromEdit),
    ).subscribe(() => {
      // closeAccount isn't required, so don't reset if it's the only field with a value being changed
      if (!this.extraPrompts.getRawValue().closeAccount) {
        this.transactionDetailsGroup.reset();
      }
    });

    combineLatest([
      this.store.select(selectOpenTwePopupConditions),
      this.store.select(selectTransactionFormHasError),
      this.store.select(selectFrequency),
      this.store.select(selectContinueClickTotalDistTriggered)
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([popupConditions, formHasError, frequency, continueClickedTotalDist]) => {
      const { verifyCallFinished, openTwePopup } = popupConditions;
      if ((frequency !== 'onetimeTotalDist' && verifyCallFinished && !openTwePopup && !formHasError) || continueClickedTotalDist) {
        this.store.dispatch(TransactionFormActions.loadReviewPage());
      }
    });

    // If payee type is valid, show the payee name
    this.payeeType$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(payeeType => {
      if (payeeType) {
        this.validPayee = true;
      } else {
        this.validPayee = false;
      }
    });

    this.firstPartyPayees$ = this.store.select(selectModifiedFirstParties);

    // If recipient is valid, show the rest of the transaction details
    this.recipientSelection$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(recipient => {
      if (recipient) {
        this.validRecipient = true;
      } else {
        this.validRecipient = false;
      }
    });

    // If second process date is valid, show second process date
    this.secondProcessDate$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
      ),
      filter(([_secondProcessDate, isFromEdit]) => isFromEdit),
      map(([secondProcessDate, _isFromEdit]) => secondProcessDate)
    ).subscribe(secondProcessDate => {
      if (secondProcessDate) {
        this.showSecondProcessDate = true;
      } else {
        this.showSecondProcessDate = false;
      }
    });

    // If is recurring and isFromEdit, show the end date even if the end date has no value
    this.isRecurring$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
      ),
      filter(([_isRecurring, isFromEdit]) => isFromEdit),
      map(([isRecurring, _isFromEdit]) => isRecurring)
    ).subscribe(isRecurring => {
      if (isRecurring) {
        this.showEndDate = true;
      } else {
        this.showEndDate = false;
      }
    });

    // IF A SLOA IS SELECTED BUILD TRANSACTIONDETAILS FORMGROUP FROM TRANSACTION FORM IN STORE
    this.store
      .select(selectUseStandingAuth)
      .pipe(
        withLatestFrom(
          this.store.select(selectTransactionFormTransactionDetails)
        ),
        filter(([useSloa, _transactionDetails]) => useSloa),
        map(([_useSloa, transactionDetails]) => transactionDetails),
        takeUntil(this.destroy$)
      )
      .subscribe((transactionDetails) => {
        buildFromPrefilledForm(
          transactionDetails,
          this.transactionDetailsGroup
        );
      });

    // If the frequency is lastBizDay, show the pay income field even if payIncome has no value
    this.recurringFrequency$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(recurringFrequency => {
      if (recurringFrequency === 'LASTBIZ') {
        this.showPayIncome = true;
      } else {
        this.showPayIncome = false;
      }
    });

    // REBUILD FORM FROM EDIT FLOW
    combineLatest([
      this.store.select(selectIsFromEdit),
      this.isIra$
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([isFromEdit, isIra]) => {
      if (isFromEdit) {
        buildFromPrefilledForm(
          this.prefilledPrompts,
          this.transactionDetailsGroup
        );
        if (!isIra) {
          //setTimeout called to wait for patchValue before setting edit state to complete
          setTimeout(() => {
            this.store.dispatch(TransactionFormActions.editLoadComplete());
          });
        } else {
          this.amountType = this.prefilledPrompts.amountType as AmountType;
        }
        this.isFromEdit$.next(true);
      }
    });

    // When recipient is changed after initial load, emit isFromEdit -> false
    this.recipientSelection$.pipe(
      skip(1),
      takeUntil(this.destroy$),
      distinctUntilChanged()
    ).subscribe(() => {
      this.isFromEdit$.next(false);
    });

    // SET RECURRING FORM FIELDS
    this.store.select(selectIsRecurring).pipe(takeUntil(this.destroy$)).subscribe(isRecurring => {
      this.isRecurringTransaction = isRecurring;
    });

    this.parentForm.controls?.transactionDetails?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value) {
        if (this.isRecurringTransaction) {
          this.parentForm.controls?.transactionDetails?.get('taxWithholdingsFormGroup')?.get('distributionReason')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            if (value) {
              if (this.recurringRMDDateError) {
                this.processDate?.setValidators(CustomValidators.futureRMDYear);
                this.processDate?.updateValueAndValidity();
              } else {
                this.processDate?.removeValidators(CustomValidators.futureRMDYear);
                this.processDate?.updateValueAndValidity();
              }
            }
          });
        }
      }
    });
  }

  onFrequencyChanged(frequency: string): void {
    this.showEndDate = true;
    if (frequency === 'SEMIMTH') {
      this.showSecondProcessDate = true;
      this.showPayIncome = false;
    } else if (frequency === 'LASTBIZ') {
      this.showPayIncome = true;
      this.showSecondProcessDate = false;
    } else {
      this.showSecondProcessDate = false;
      this.showPayIncome = false;
    }
  }

  get recurringRMDDateError(): boolean {
    if (this.parentForm.controls?.transactionDetails?.get('taxWithholdingsFormGroup')?.get('distributionReason')?.value === distributionReasons.rmd) {
      return true;
    }
    return false;
  }

  get processDate() {
    return this.parentForm.controls?.transactionDetails?.get('processDate');
  }


  get recipient(): FormControl<typeof RecipientValues[number]> {
    return this.transactionDetailsGroup.get('recipient') as FormControl<typeof RecipientValues[number]>;
  }

  get minDate(): Date {
    return new Date();
  }

  get maxDate(): Date {
    let maxDate: Date = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    return maxDate;
  }

  get extraPrompts(): FormGroup<ExtraPromptsForm> {
    return this.parentForm.get('extraPrompts') as FormGroup<ExtraPromptsForm>;
  }

  get overdraftRulesFutureDate(): Date {
    let today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 1));
  }

  get enableModifiedPayee(): boolean {
    return !this.isFromEditTable;
  }

  get taxWithholdingsFormGroupDetails() {
    return (this.editFormDetails?.transactionDetails as CheckTransactionDetails)?.taxWithholdingsFormGroup;
  }

  setShowOvernightWarning(showOvernightWarning: boolean): void {
    this.overnightCheckFailure.emit(showOvernightWarning);
  }

  getFrequencyType(extraPrompts: any) {
    // use frequency from transaction details for review/confirmation pages as value is set by user in input step
    // use frequency from extraPrompts when user view the details from table
    // use recurringFrequency from transaction details when standing Auth is yes and user view the details from table
    if (extraPrompts?.getRawValue().useStandingAuth === 'yes' || (extraPrompts?.getRawValue().useStandingAuth === this.bundled && extraPrompts?.getRawValue().useBundledStandingAuth === yesNo.yes)) {
      return extraPrompts.getRawValue().frequency;
    }
    return extraPrompts.getRawValue().frequency;
  }

  getFrequency(extraPrompts: any, frequency: string): string {
    let extraPrompt = extraPrompts.getRawValue();

    if (extraPrompt.useStandingAuth === this.bundled) {
      if (extraPrompt.useBundledStandingAuth === Frequencies.recurring || (extraPrompt.useBundledStandingAuth === yesNo.yes && frequency === Frequencies.recurring)) {
        return frequencyDict.recurring;
      } else if (extraPrompt.useBundledStandingAuth === yesNo.no || (extraPrompt.useBundledStandingAuth === yesNo.yes && (frequency === Frequencies.onetime || frequency === Frequencies.onRequest))) {
        return frequencyDict.onetime;
      } else if (extraPrompt.useBundledStandingAuth === this.onetimeTotalDist || (extraPrompt.useBundledStandingAuth === yesNo.yes && frequency === this.onetimeTotalDist)) {
        return frequencyDict.onetimeTotalDist;
      } else {
        return '';
      }
    } else if (extraPrompt.useStandingAuth === yesNo.yes && !!frequency) {
      if (frequency === Frequencies.onetime || frequency === Frequencies.onRequest) {
        return frequencyDict.onetime;
      } else if (frequency === this.onetimeTotalDist) {
        return frequencyDict.onetimeTotalDist;
      } else {
        return frequencyDict.recurring;
      }
    }
    return extraPrompt?.frequency;
  }

  isProcessDateInFutureYear(date: string): boolean {
    if (date) {
      let curYear = new Date().getFullYear();
      let yearSelected = new Date(date).getFullYear();
      return yearSelected > curYear;
    }
    return false;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
